import React, { CSSProperties, useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { useIsAdmin, useIsGlobalAdmin, useHasPermission, ROLE_LABELS } from './lib/auth';
import {
  LogOut, Users, GraduationCap, BookOpen, Layers, PencilLine, FileText,
  Settings, LayoutDashboard, ChevronRight, HelpCircle, BarChart3
} from 'lucide-react';
import { getCenters } from './lib/db';
import type { Center } from './types';
import { CustomSelect } from './components/ui/CustomSelect';
import { NotificationBell } from './components/ui/NotificationBell';
import { useI18n, initLanguage } from './lib/i18n';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { Students } from './components/Students';
import { Courses } from './components/Courses';
import { ReportCards } from './components/ReportCards';
import { Settings as SettingsPage } from './components/Settings';
import { Teachers } from './components/Teachers';
import { TeacherSubjects } from './components/TeacherSubjects';
import { GradesInput } from './components/grades/GradesInput';
import { Help } from './components/Help';
import { AdvancedStats } from './components/AdvancedStats';
import { LandingPage } from './components/LandingPage';

const dragStyle: CSSProperties = { WebkitAppRegion: 'drag' } as CSSProperties;
const noDragStyle: CSSProperties = { WebkitAppRegion: 'no-drag' } as CSSProperties;

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuth((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function Sidebar() {
  const logout = useAuth((state) => state.logout);
  const user = useAuth((state) => state.user);
  const switchCenter = useAuth((state) => state.switchCenter);
  const isAdmin = useIsAdmin();
  const isGlobalAdmin = useIsGlobalAdmin();
  const canViewStudents = useHasPermission('view_students');
  const canViewTeachers = useHasPermission('view_teachers');
  const canViewCourses = useHasPermission('view_courses');
  const canViewGrades = useHasPermission('view_grades');
  const canViewTeacherSubjects = useHasPermission('view_teacher_subjects');
  const [centers, setCenters] = useState<Center[]>([]);
  const { t } = useI18n();

  useEffect(() => {
    initLanguage();
  }, []);

  useEffect(() => {
    if (isGlobalAdmin) {
      getCenters().then(setCenters).catch(console.error);
    }
  }, [isGlobalAdmin]);

  // Agrupado y ordenado según el flujo real de trabajo: primero se organiza
  // el centro (cursos -> profesores -> alumnos -> asignaturas), después se
  // evalúa, y lo de sistema queda al final. Cada entrada tiene un icono
  // propio: si se repiten, dejan de servir para orientarse de un vistazo.
  const navGroups = [
    { title: null, items: [
      { label: t('ui_home'), href: '/', icon: LayoutDashboard, show: true, exact: true as boolean | undefined },
    ]},
    { title: t('ui_group_organization'), items: [
      { label: t('ui_courses'), href: '/courses', icon: Layers, show: canViewCourses || isAdmin },
      { label: t('ui_teachers'), href: '/teachers', icon: GraduationCap, show: canViewTeachers || isAdmin },
      { label: t('ui_students'), href: '/students', icon: Users, show: canViewStudents || isAdmin },
      { label: t('ui_subjects'), href: '/teacher-subjects', icon: BookOpen, show: canViewTeacherSubjects || isAdmin },
    ]},
    { title: t('ui_group_evaluation'), items: [
      { label: t('ui_grades'), href: '/grades', icon: PencilLine, show: canViewGrades || isAdmin },
      { label: t('ui_reportCards'), href: '/report-cards', icon: FileText, show: isAdmin },
      { label: t('ui_statistics'), href: '/stats', icon: BarChart3, show: isAdmin },
    ]},
    { title: t('ui_group_system'), items: [
      { label: t('ui_settings'), href: '/settings', icon: Settings, show: isAdmin },
      { label: t('ui_help'), href: '/help', icon: HelpCircle, show: true },
    ]},
  ]
    .map(g => ({ ...g, items: (g.items as { label: string; href: string; icon: typeof Users; show: boolean; exact?: boolean }[]).filter(i => i.show) }))
    .filter(g => g.items.length > 0);

  return (
    <aside className="flex fixed top-0 left-0 bottom-0 z-50 flex-col w-64 bg-slate-900 select-none">
      {/* Drag region + logo */}
      <div style={dragStyle} className="h-8 bg-slate-900 shrink-0" />
      <div className="flex items-center gap-3 px-5 pt-2 pb-5" style={noDragStyle}>
        <img src="./icon.png" alt="Logo" className="w-9 h-9 rounded-lg shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">Escuela de Música</p>
          <p className="text-xs text-slate-400 truncate">Gestión Académica</p>
        </div>
      </div>

      <div className="mx-4 border-b border-slate-700/60" />

      {/* Admin center switcher */}
      {isGlobalAdmin && centers.length > 0 && (
        <div className="px-4 py-3" style={noDragStyle}>
          <label className="block mb-1 text-xs font-medium text-slate-400">Centro activo</label>
          <CustomSelect
            value={user?.centerId || ''}
            onValueChange={(v) => switchCenter(v)}
            options={centers.map(c => ({ value: c.id, label: c.name }))}
            size="sm"
            variant="dark"
          />
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 px-3 pt-3 pb-2 overflow-y-auto" style={noDragStyle}>
        {navGroups.map((group, gi) => (
          <div key={group.title ?? 'inicio'} className={gi > 0 ? 'mt-5' : ''}>
            {group.title && (
              <p className="px-3 mb-1.5 text-[11px] font-semibold tracking-wider text-slate-500 uppercase">{group.title}</p>
            )}
            <div className="space-y-0.5">
              {group.items.map(({ label, href, icon: Icon, exact }) => (
                <NavLink
                  key={href}
                  to={href}
                  end={exact}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 group ${
                      isActive
                        ? 'bg-indigo-600 text-white font-medium'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'}`} />
                      <span className="flex-1 truncate">{label}</span>
                      {isActive && <ChevronRight className="w-3.5 h-3.5 text-indigo-300 shrink-0" />}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User + logout */}
      <div className="shrink-0 p-3 border-t border-slate-700/60" style={noDragStyle}>
        <div className="flex items-center gap-3 px-2 py-2 mb-1 rounded-lg">
          <div className="flex justify-center items-center w-8 h-8 bg-indigo-700 rounded-full shrink-0">
            <span className="text-xs font-bold text-white">
              {(user?.name || user?.username)?.[0]?.toUpperCase() ?? 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-200 truncate">{user?.name || user?.username}</p>
            <p className="text-xs text-slate-500 truncate">{user ? ROLE_LABELS[user.role] : ''}</p>
          </div>
          <NotificationBell />
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 px-3 py-2 w-full text-sm text-slate-400 rounded-lg transition-colors hover:bg-slate-800 hover:text-red-400"
        >
          <LogOut className="w-4 h-4" />
          <span>{t('ui_logout')}</span>
        </button>
      </div>
    </aside>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const centerId = useAuth((state) => state.user?.centerId);
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main key={centerId} className="flex-1 ml-64 h-screen overflow-y-auto">
        {children}
      </main>
    </div>
  );
}

function App() {
  const isAuthenticated = useAuth((state) => state.isAuthenticated);
  const isElectron = typeof window !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');

  return (
    <Router>
      {isAuthenticated ? (
        <AppLayout>
          <Routes>
            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/students" element={<PrivateRoute><Students /></PrivateRoute>} />
            <Route path="/courses" element={<PrivateRoute><Courses /></PrivateRoute>} />
            <Route path="/report-cards" element={<PrivateRoute><ReportCards /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
            <Route path="/teachers" element={<PrivateRoute><Teachers /></PrivateRoute>} />
            <Route path="/teacher-subjects" element={<PrivateRoute><TeacherSubjects /></PrivateRoute>} />
            <Route path="/grades" element={<PrivateRoute><GradesInput /></PrivateRoute>} />
            <Route path="/centers" element={<Navigate to="/settings?tab=centers" replace />} />
            <Route path="/stats" element={<PrivateRoute><AdvancedStats /></PrivateRoute>} />
            <Route path="/help" element={<PrivateRoute><Help /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </AppLayout>
      ) : (
        <Routes>
          <Route path="/" element={isElectron ? <Login /> : <LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      )}
    </Router>
  );
}

export default App;